import { useState } from "react";
import BrandLogo from "@/components/BrandLogo";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { Loader2, ArrowRight, Trophy, Repeat, ShieldCheck } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";
import PlayerProfileFields, { validatePlayerProfile } from "@/components/PlayerProfileFields";

// Google users land here after first login (account_type === null).
export default function CompleteProfile() {
  const nav = useNavigate();
  const { user, completeProfile } = useAuth();
  const [accountType, setAccountType] = useState(null);
  const [profile, setProfile] = useState({});
  const [busy, setBusy] = useState(false);

  if (user && user.account_type) { nav("/dashboard", { replace: true }); return null; }

  const submit = async () => {
    if (accountType === "player") {
      const miss = validatePlayerProfile(profile);
      if (miss.length) { toast.error(`Please complete: ${miss.join(", ")}`); return; }
    }
    setBusy(true);
    try {
      await completeProfile(accountType, accountType === "player" ? profile : undefined);
      toast.success("You're all set!");
      nav("/dashboard", { replace: true });
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Could not save"); }
    finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-4 py-12 cyber-grid" style={{ background: "var(--bg)", color: "var(--text)" }}>
      <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} className={accountType === "player" ? "w-full max-w-lg" : "w-full max-w-md"} data-testid="complete-profile">
        <div className="flex items-center gap-2 mb-2"><ShieldCheck className="w-5 h-5 text-primary-c" /><span className="font-display font-black tracking-tighter"><BrandLogo height={24} /></span></div>
        <span className="text-xs tracking-[0.25em] uppercase font-bold text-primary-c">Finish setup</span>
        <h1 className="font-display font-bold tracking-tight text-3xl mt-2">Welcome{user?.name ? `, ${user.name.split(" ")[0]}` : ""}!</h1>
        <p className="mt-2 text-sm" style={{ color: "var(--text-2)" }}>Choose how you'll use PointControl.</p>

        {!accountType ? (
          <div className="grid gap-4 mt-8">
            <button data-testid="cp-pick-organizer" onClick={() => setAccountType("organizer")} className="text-left rounded-2xl glow-border p-5 hover:scale-[1.01] transition-transform" style={{ background: "var(--surface)" }}>
              <div className="w-11 h-11 rounded-xl glass flex items-center justify-center mb-3"><Repeat className="w-5 h-5 text-primary-c" /></div>
              <div className="font-display font-bold">I'm an Organizer</div>
              <p className="text-sm mt-1" style={{ color: "var(--text-2)" }}>Run open-play and create tournaments (subscription required).</p>
            </button>
            <button data-testid="cp-pick-player" onClick={() => setAccountType("player")} className="text-left rounded-2xl glow-border p-5 hover:scale-[1.01] transition-transform" style={{ background: "var(--surface)" }}>
              <div className="w-11 h-11 rounded-xl glass flex items-center justify-center mb-3"><Trophy className="w-5 h-5 text-primary-c" /></div>
              <div className="font-display font-bold">I'm a Player</div>
              <p className="text-sm mt-1" style={{ color: "var(--text-2)" }}>Register for tournaments and manage your profile.</p>
            </button>
          </div>
        ) : (
          <div className="mt-6">
            <button data-testid="cp-change-type" onClick={() => setAccountType(null)} className="text-sm mb-4 hover:text-primary-c transition-colors" style={{ color: "var(--text-2)" }}>← Change ({accountType})</button>
            {accountType === "player" && <PlayerProfileFields value={profile} onChange={setProfile} />}
            <button data-testid="cp-submit" onClick={submit} disabled={busy} className="btn-glow w-full rounded-xl py-3 mt-6 font-bold flex items-center justify-center gap-2 disabled:opacity-60">
              {busy ? <Loader2 className="w-5 h-5 animate-spin" /> : <>Continue <ArrowRight className="w-4 h-4" /></>}
            </button>
          </div>
        )}
      </motion.div>
    </div>
  );
}
